import React, { useState } from 'react';

const WhatWeDo = () => {
  const [activeCard, setActiveCard] = useState(0);

  const colors = {
    white: '#ffffff',
    page: '#f4f9ff',
    pageSoft: '#eaf4ff',

    navy: '#071a33',
    navyLight: '#163a63',

    blue: '#0879d1',
    blueBright: '#0ea5e9',
    cyan: '#22d3ee',

    text: '#102b47',
    textLight: '#58718b',

    border: '#d7e7f5',
    borderBlue: '#a9d6f5',

    muted: '#8ca3b8',
  };

  const servicesData = [
    {
      num: '01',
      short: 'STRUCTURAL',
      title: 'Structural Analysis & Design',
      desc: 'Complete structural analysis and design of steel and composite structures with an absolute focus on optimization, structural integrity, safety, and code compliance.',
      tags: ['Steel Structures', 'Optimization', 'Code Compliant'],
      icon: '◇',
      metric: '01',
    },
    {
      num: '02',
      short: 'STAAD.PRO',
      title: 'Advanced STAAD.Pro Modelling',
      desc: 'Modern STAAD.Pro workflows to model, analyze, and simulate complex structural systems while maintaining safety, efficiency, and cost-effectiveness.',
      tags: ['STAAD.Pro', 'Finite Element', 'Simulation'],
      icon: '⌬',
      metric: '02',
    },
    {
      num: '03',
      short: 'CONNECTIONS',
      title: 'Steel Connection Design & Verification',
      desc: 'Rigorous connection design and verification using IDEA StatiCa and RAM Connection to guarantee joint safety, structural performance, and constructability.',
      tags: ['IDEA StatiCa', 'RAM Connection', 'Verification'],
      icon: '⊞',
      metric: '03',
    },
    {
      num: '04',
      short: 'TEKLA',
      title: 'Tekla Modelling & Shop Drawings',
      desc: 'Precise 3D structural models and fabrication-oriented shop drawings that bridge the gap between engineering design and real-world site execution.',
      tags: ['Tekla Structures', 'Shop Drawings', '3D Modeling'],
      icon: '▱',
      metric: '04',
    },
    {
      num: '05',
      short: 'LAND',
      title: 'Land Development & Survey',
      desc: 'Transformation of raw survey data into detailed finished survey drawings, boundary plans, topographic maps, ALTA/NSPS maps, and surface contours.',
      tags: ['Civil 3D', 'Topographic', 'Site Grading'],
      icon: '⌖',
      metric: '05',
    },
    {
      num: '06',
      short: 'CAD',
      title: 'Comprehensive CAD Drafting',
      desc: 'Precise engineering draftsmanship supporting site layouts, drainage design, utility mapping, roadway profiles, and quantity calculations.',
      tags: ['AutoCAD', 'Civil 3D', 'Infrastructure'],
      icon: '▤',
      metric: '06',
    },
  ];

  const activeService = servicesData[activeCard];

  const styles = {
    section: {
      position: 'relative',
      width: '100%',
      minHeight: '100vh',
      padding: '95px 5%',
      boxSizing: 'border-box',
      overflow: 'hidden',
      fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
      background: `
        radial-gradient(
          circle at 5% 20%,
          rgba(14,165,233,0.10),
          transparent 24%
        ),
        radial-gradient(
          circle at 95% 75%,
          rgba(34,211,238,0.09),
          transparent 26%
        ),
        ${colors.page}
      `,
    },

    grid: {
      position: 'absolute',
      inset: 0,
      backgroundImage: `
        linear-gradient(
          rgba(8,121,209,0.045) 1px,
          transparent 1px
        ),
        linear-gradient(
          90deg,
          rgba(8,121,209,0.045) 1px,
          transparent 1px
        )
      `,
      backgroundSize: '45px 45px',
      animation: 'serviceGridMove 22s linear infinite',
      pointerEvents: 'none',
    },

    dotGrid: {
      position: 'absolute',
      inset: 0,
      backgroundImage:
        'radial-gradient(rgba(8,121,209,0.18) 1px, transparent 1px)',
      backgroundSize: '25px 25px',
      opacity: 0.22,
      animation: 'serviceDots 15s linear infinite',
      pointerEvents: 'none',
    },

    glowOne: {
      position: 'absolute',
      width: '400px',
      height: '400px',
      borderRadius: '50%',
      left: '-180px',
      top: '80px',
      background:
        'radial-gradient(circle, rgba(14,165,233,0.15), transparent 70%)',
      filter: 'blur(10px)',
      animation: 'serviceGlow 9s ease-in-out infinite',
      pointerEvents: 'none',
    },

    glowTwo: {
      position: 'absolute',
      width: '430px',
      height: '430px',
      borderRadius: '50%',
      right: '-180px',
      bottom: '-100px',
      background:
        'radial-gradient(circle, rgba(34,211,238,0.13), transparent 70%)',
      filter: 'blur(10px)',
      animation: 'serviceGlowReverse 11s ease-in-out infinite',
      pointerEvents: 'none',
    },

    lineOne: {
      position: 'absolute',
      width: '320px',
      height: '1px',
      left: '-80px',
      top: '35%',
      background:
        'linear-gradient(90deg, transparent, rgba(8,121,209,0.35), transparent)',
      transform: 'rotate(-25deg)',
      animation: 'serviceLineOne 8s ease-in-out infinite',
      pointerEvents: 'none',
    },

    lineTwo: {
      position: 'absolute',
      width: '300px',
      height: '1px',
      right: '-70px',
      top: '25%',
      background:
        'linear-gradient(90deg, transparent, rgba(34,211,238,0.35), transparent)',
      transform: 'rotate(25deg)',
      animation: 'serviceLineTwo 9s ease-in-out infinite',
      pointerEvents: 'none',
    },

    container: {
      position: 'relative',
      zIndex: 5,
      width: '100%',
      maxWidth: '1240px',
      margin: '0 auto',
    },

    header: {
      maxWidth: '850px',
      margin: '0 auto 55px',
      textAlign: 'center',
      animation: 'serviceFadeUp 0.9s ease both',
    },

    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '9px',
      padding: '9px 16px',
      borderRadius: '999px',
      background: 'rgba(255,255,255,0.78)',
      border: `1px solid ${colors.borderBlue}`,
      color: colors.blue,
      fontSize: '0.74rem',
      fontWeight: '750',
      letterSpacing: '0.13em',
      textTransform: 'uppercase',
      boxShadow: '0 8px 30px rgba(8,121,209,0.07)',
      backdropFilter: 'blur(14px)',
      WebkitBackdropFilter: 'blur(14px)',
    },

    badgeDot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      background: colors.cyan,
      boxShadow: '0 0 0 5px rgba(34,211,238,0.10)',
      animation: 'servicePulse 2s ease-in-out infinite',
    },

    heading: {
      margin: '25px 0 0',
      fontSize: 'clamp(2.5rem, 5vw, 4.5rem)',
      lineHeight: '1.03',
      letterSpacing: '-0.05em',
      fontWeight: '850',
      color: colors.navy,
    },

    gradientText: {
      background:
        'linear-gradient(100deg, #0879d1, #0ea5e9 50%, #22d3ee)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },

    description: {
      maxWidth: '760px',
      margin: '22px auto 0',
      fontSize: '1.04rem',
      lineHeight: '1.75',
      color: colors.textLight,
    },

    mainLayout: {
      display: 'grid',
      gridTemplateColumns: '280px minmax(0, 1fr)',
      gap: '25px',
      alignItems: 'stretch',
    },

    navigation: {
      display: 'flex',
      flexDirection: 'column',
      gap: '7px',
      padding: '10px',
      borderRadius: '22px',
      background: 'rgba(255,255,255,0.70)',
      border: `1px solid ${colors.border}`,
      boxShadow: '0 15px 40px rgba(23,83,132,0.07)',
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
    },

    navItem: (active) => ({
      position: 'relative',
      display: 'grid',
      gridTemplateColumns: '45px 1fr',
      alignItems: 'center',
      gap: '10px',
      padding: '15px 13px',
      minHeight: '68px',
      borderRadius: '15px',
      border: active
        ? `1px solid ${colors.borderBlue}`
        : '1px solid transparent',
      background: active
        ? 'linear-gradient(135deg, rgba(8,121,209,0.09), rgba(34,211,238,0.07))'
        : 'transparent',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    }),

    navNumber: (active) => ({
      width: '40px',
      height: '40px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.76rem',
      fontWeight: '800',
      color: active ? '#ffffff' : colors.blue,
      background: active
        ? 'linear-gradient(135deg, #0879d1, #22d3ee)'
        : '#eef7ff',
      transition: 'all 0.3s ease',
      boxShadow: active
        ? '0 8px 20px rgba(8,121,209,0.22)'
        : 'none',
    }),

    navText: (active) => ({
      color: active ? colors.navy : colors.textLight,
      fontSize: '0.78rem',
      fontWeight: active ? '750' : '650',
      letterSpacing: '0.03em',
      transition: 'color 0.3s ease',
    }),

    activeBar: {
      position: 'absolute',
      left: 0,
      top: '20%',
      width: '3px',
      height: '60%',
      borderRadius: '5px',
      background:
        'linear-gradient(#0879d1, #22d3ee)',
    },

    featurePanel: {
      position: 'relative',
      overflow: 'hidden',
      minHeight: '475px',
      padding: '40px',
      borderRadius: '25px',
      background: `
        radial-gradient(
          circle at 90% 5%,
          rgba(14,165,233,0.18),
          transparent 28%
        ),
        linear-gradient(
          135deg,
          rgba(255,255,255,0.94),
          rgba(239,247,255,0.88)
        )
      `,
      border: `1px solid ${colors.border}`,
      boxShadow: '0 25px 70px rgba(23,83,132,0.10)',
      backdropFilter: 'blur(18px)',
      WebkitBackdropFilter: 'blur(18px)',
      animation: 'servicePanelIn 0.55s ease',
    },

    panelTop: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '20px',
    },

    panelLabel: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      color: colors.blue,
      fontSize: '0.72rem',
      fontWeight: '800',
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
    },

    statusDot: {
      width: '7px',
      height: '7px',
      borderRadius: '50%',
      background: colors.cyan,
      boxShadow: '0 0 0 5px rgba(34,211,238,0.10)',
    },

    bigNumber: {
      fontSize: '5rem',
      lineHeight: 1,
      fontWeight: '900',
      letterSpacing: '-0.08em',
      color: 'rgba(8,121,209,0.075)',
      userSelect: 'none',
    },

    panelIcon: {
      width: '72px',
      height: '72px',
      borderRadius: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginTop: '38px',
      background:
        'linear-gradient(135deg, rgba(8,121,209,0.12), rgba(34,211,238,0.15))',
      border: `1px solid ${colors.borderBlue}`,
      color: colors.blue,
      fontSize: '2rem',
      fontWeight: '800',
      animation: 'serviceIconFloat 4s ease-in-out infinite',
    },

    panelTitle: {
      margin: '24px 0 0',
      maxWidth: '700px',
      fontSize: 'clamp(1.8rem, 3vw, 2.8rem)',
      lineHeight: '1.12',
      letterSpacing: '-0.04em',
      fontWeight: '820',
      color: colors.navy,
    },

    panelDescription: {
      maxWidth: '680px',
      margin: '17px 0 0',
      color: colors.textLight,
      fontSize: '0.97rem',
      lineHeight: '1.75',
    },

    tags: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '9px',
      marginTop: '27px',
    },

    tag: {
      padding: '7px 11px',
      borderRadius: '8px',
      background: '#ffffff',
      border: `1px solid ${colors.border}`,
      color: colors.navyLight,
      fontSize: '0.73rem',
      fontWeight: '700',
      boxShadow: '0 4px 12px rgba(8,121,209,0.04)',
    },

    panelBottom: {
      position: 'absolute',
      left: '40px',
      right: '40px',
      bottom: '30px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '20px',
    },

    bottomText: {
      color: colors.muted,
      fontSize: '0.72rem',
      fontWeight: '700',
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },

    progressTrack: {
      width: '160px',
      height: '4px',
      borderRadius: '10px',
      background: '#dcebf7',
      overflow: 'hidden',
    },

    progress: {
      height: '100%',
      width: `${((activeCard + 1) / servicesData.length) * 100}%`,
      borderRadius: '10px',
      background:
        'linear-gradient(90deg, #0879d1, #22d3ee)',
      transition: 'width 0.45s ease',
    },
  };

  return (
    <section style={styles.section}>
      <style>{`
        * {
          box-sizing: border-box;
        }

        @keyframes serviceGridMove {
          0% {
            background-position: 0 0;
          }

          100% {
            background-position: 45px 45px;
          }
        }

        @keyframes serviceDots {
          0% {
            transform: translate(0,0);
          }

          50% {
            transform: translate(10px,-10px);
          }

          100% {
            transform: translate(0,0);
          }
        }

        @keyframes serviceGlow {
          0%,100% {
            transform: translate(0,0) scale(1);
          }

          50% {
            transform: translate(40px,-25px) scale(1.08);
          }
        }

        @keyframes serviceGlowReverse {
          0%,100% {
            transform: translate(0,0) scale(1);
          }

          50% {
            transform: translate(-35px,25px) scale(1.08);
          }
        }

        @keyframes serviceLineOne {
          0%,100% {
            transform: translateX(0) rotate(-25deg);
            opacity: 0.15;
          }

          50% {
            transform: translateX(130px) rotate(-25deg);
            opacity: 0.65;
          }
        }

        @keyframes serviceLineTwo {
          0%,100% {
            transform: translateX(0) rotate(25deg);
            opacity: 0.15;
          }

          50% {
            transform: translateX(-120px) rotate(25deg);
            opacity: 0.65;
          }
        }

        @keyframes serviceFadeUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }

          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes servicePanelIn {
          from {
            opacity: 0;
            transform: translateX(20px);
          }

          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes servicePulse {
          0%,100% {
            transform: scale(1);
          }

          50% {
            transform: scale(1.25);
          }
        }

        @keyframes serviceIconFloat {
          0%,100% {
            transform: translateY(0);
          }

          50% {
            transform: translateY(-6px);
          }
        }

        @media (max-width: 950px) {
          .services-main-layout {
            grid-template-columns: 220px minmax(0,1fr) !important;
          }

          .services-navigation {
            padding: 7px !important;
          }

          .services-nav-item {
            grid-template-columns: 38px 1fr !important;
          }
        }

        @media (max-width: 760px) {
          .services-section {
            padding: 70px 20px !important;
          }

          .services-main-layout {
            grid-template-columns: 1fr !important;
          }

          .services-navigation {
            display: grid !important;
            grid-template-columns: repeat(3, 1fr) !important;
            gap: 7px !important;
          }

          .services-nav-item {
            grid-template-columns: 1fr !important;
            text-align: center !important;
            justify-items: center !important;
            min-height: 82px !important;
            padding: 10px !important;
          }

          .services-active-bar {
            display: none !important;
          }

          .services-feature-panel {
            min-height: 510px !important;
            padding: 28px !important;
          }

          .services-panel-bottom {
            left: 28px !important;
            right: 28px !important;
            bottom: 22px !important;
          }

          .services-panel-title {
            font-size: 2rem !important;
          }
        }

        @media (max-width: 480px) {
          .services-section {
            padding: 60px 15px !important;
          }

          .services-heading {
            font-size: 2.45rem !important;
          }

          .services-description {
            font-size: 0.92rem !important;
          }

          .services-navigation {
            grid-template-columns: repeat(2, 1fr) !important;
          }

          .services-feature-panel {
            min-height: 550px !important;
            padding: 23px !important;
            border-radius: 20px !important;
          }

          .services-panel-title {
            font-size: 1.75rem !important;
          }

          .services-panel-description {
            font-size: 0.9rem !important;
          }

          .services-panel-bottom {
            left: 23px !important;
            right: 23px !important;
          }

          .services-progress {
            width: 100px !important;
          }

          .services-big-number {
            font-size: 3.8rem !important;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          *,
          *::before,
          *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>

      {/* Background */}
      <div style={styles.grid} />
      <div style={styles.dotGrid} />
      <div style={styles.glowOne} />
      <div style={styles.glowTwo} />
      <div style={styles.lineOne} />
      <div style={styles.lineTwo} />

      <div
        style={styles.container}
        className="services-section"
      >
        {/* ================= HEADER ================= */}

        <div style={styles.header}>
          <div style={styles.badge}>
            <span style={styles.badgeDot} />
            Our Capabilities & Expertise
          </div>

          <h2
            style={styles.heading}
            className="services-heading"
          >
            Engineering Solutions
            <br />
            <span style={styles.gradientText}>
              Built for Real-World Performance
            </span>
          </h2>

          <p
            style={styles.description}
            className="services-description"
          >
            Combining structural expertise, advanced digital
            workflows, and precision drafting to deliver safe,
            efficient, economical, and buildable solutions
            across industrial, commercial, and infrastructure
            projects.
          </p>
        </div>

        {/* ================= SERVICE DASHBOARD ================= */}

        <div
          style={styles.mainLayout}
          className="services-main-layout"
        >
          {/* LEFT NAVIGATION */}

          <div
            style={styles.navigation}
            className="services-navigation"
          >
            {servicesData.map((service, index) => {
              const active = activeCard === index;

              return (
                <div
                  key={service.num}
                  style={styles.navItem(active)}
                  className="services-nav-item"
                  onMouseEnter={() => setActiveCard(index)}
                  onClick={() => setActiveCard(index)}
                >
                  {active && (
                    <span
                      style={styles.activeBar}
                      className="services-active-bar"
                    />
                  )}

                  <div style={styles.navNumber(active)}>
                    {service.num}
                  </div>

                  <div style={styles.navText(active)}>
                    {service.short}
                  </div>
                </div>
              );
            })}
          </div>

          {/* FEATURE PANEL */}

          <div
            style={styles.featurePanel}
            className="services-feature-panel"
            key={activeService.num}
          >
            <div style={styles.panelTop}>
              <div style={styles.panelLabel}>
                <span style={styles.statusDot} />
                Engineering Capability
              </div>

              <div
                style={styles.bigNumber}
                className="services-big-number"
              >
                {activeService.metric}
              </div>
            </div>

            <div style={styles.panelIcon}>
              {activeService.icon}
            </div>

            <h3
              style={styles.panelTitle}
              className="services-panel-title"
            >
              {activeService.title}
            </h3>

            <p
              style={styles.panelDescription}
              className="services-panel-description"
            >
              {activeService.desc}
            </p>

            <div style={styles.tags}>
              {activeService.tags.map((tag) => (
                <span key={tag} style={styles.tag}>
                  {tag}
                </span>
              ))}
            </div>

            <div
              style={styles.panelBottom}
              className="services-panel-bottom"
            >
              <span style={styles.bottomText}>
                SANSA ENGINEERING SERVICES
              </span>

              <div
                style={styles.progressTrack}
                className="services-progress"
              >
                <div style={styles.progress} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhatWeDo;